import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { FiscalBanner } from '@/components/FiscalBanner';
import { FiscalCountdown } from '@/components/FiscalCountdown';
import { FiscalSimulator } from '@/components/FiscalSimulator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Wrench, ArrowRight, Check, X, Star, Clock, Shield, Phone, 
  AlertTriangle, TrendingDown, Calculator, ChevronRight, ChefHat, Bath, Home
} from 'lucide-react';

// Images - Rénovation moderne
const HERO_IMAGE = "https://images.unsplash.com/photo-1759150712360-6d48015e4f86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxyZW5vdmF0aW9uJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBtb2Rlcm4lMjBob21lfGVufDB8fHx8MTc3NDEyOTA1OHww&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_1 = "https://images.unsplash.com/photo-1773101883573-2afcb4c4ff4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxyZW5vdmF0aW9uJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBtb2Rlcm4lMjBob21lfGVufDB8fHx8MTc3NDEyOTA1OHww&ixlib=rb-4.1.0&q=85";

export const RenovationPage = () => {
  const formRef = useRef(null);
  const simulatorRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSimulator = () => {
    simulatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const questions = [
    { id: 'type_reno', label: 'Type de rénovation ?', type: 'select', options: ['Complète', 'Salle de bain', 'Cuisine', 'Peinture', 'Parquet', 'Carrelage', 'Autre'] },
    { id: 'type_bien', label: 'Appartement ou maison ?', type: 'select', options: ['Appartement', 'Maison', 'Chalet', 'Local commercial'] },
    { id: 'localisation', label: 'Localisation ?', type: 'text', placeholder: 'Ex: Lausanne, Genève...' },
    { id: 'surface', label: 'Surface concernée ?', type: 'text', placeholder: 'Ex: 80 m²' },
    { id: 'budget', label: 'Budget estimatif ?', type: 'text', placeholder: 'Ex: 25\'000 CHF' },
    { id: 'optimisation_fiscale', label: 'Souhaitez-vous optimiser fiscalement ?', type: 'select', options: ['Oui, c\'est prioritaire', 'J\'aimerais en savoir plus', 'Non, pas nécessaire'] }
  ];

  // Exemples d'impact fiscal pour les services
  const fiscalExamples = [
    { service: 'Cuisine complète', budget: 35000, taux: 25 },
    { service: 'Salle de bain', budget: 25000, taux: 25 },
    { service: 'Rénovation complète', budget: 80000, taux: 25 },
    { service: 'Peinture intérieure', budget: 8000, taux: 25 }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="renovation-page">
      {/* Bannière fiscale urgente */}
      <FiscalBanner onSimulatorClick={scrollToSimulator} />
      
      <Header />
      
      {/* ============================================
          HERO - ANGLE FISCAL URGENT
          ============================================ */}
      <section className="hero-section relative min-h-[75vh] sm:min-h-[80vh] md:min-h-[85vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Rénovation intérieure moderne" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Wrench className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-orange-500 text-white border-orange-400 text-xs sm:text-sm">
                Fenêtre fiscale limitée
              </Badge>
            </div>
            
            {/* TITRE FISCAL */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Aujourd'hui, vos travaux sont partiellement financés par vos impôts.
              <span className="block text-orange-300 mt-2 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl">Demain, ce sera 100% à votre charge.</span>
            </h1>
            
            {/* SOUS-TEXTE */}
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-3 sm:mb-4 max-w-2xl">
              Suite à la réforme votée en Suisse, les déductions fiscales liées aux rénovations 
              vont être supprimées progressivement.
            </p>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white font-semibold mb-5 sm:mb-8 max-w-2xl">
              Aujourd'hui, vous pouvez encore réduire votre coût réel.
              <br />Demain, vous paierez plein tarif.
            </p>

            {/* Compte à rebours compact */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 mb-5 sm:mb-8 inline-block">
              <FiscalCountdown />
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full bg-orange-500 hover:bg-orange-600 w-full sm:w-auto"
                onClick={scrollToSimulator}
                data-testid="renovation-cta-simulator"
              >
                <Calculator className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Calculer mes économies
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)] backdrop-blur-sm w-full sm:w-auto"
                onClick={scrollToForm}
              >
                <TrendingDown className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Voir combien je perds
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION PROBLÈME
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="mb-3 sm:mb-4 bg-red-100 text-red-700 text-xs sm:text-sm">Le constat</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 px-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Attendre vos travaux vous coûte déjà de l'argent.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto">
            {/* Aujourd'hui */}
            <div className="bg-green-50 border-2 border-green-200 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-green-800 mb-4 sm:mb-6 flex items-center gap-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full"></div>
                Aujourd'hui
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  'Une partie de vos travaux est déductible',
                  'Vous réduisez votre imposition',
                  'Votre coût réel est optimisé'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 sm:gap-3">
                    <Check className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-green-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Demain */}
            <div className="bg-red-50 border-2 border-red-200 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-red-800 mb-4 sm:mb-6 flex items-center gap-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-500 rounded-full"></div>
                Demain (après réforme)
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  'Plus de déduction fiscale',
                  'Même travaux, coût plus élevé',
                  'Aucune optimisation possible'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 sm:gap-3">
                    <X className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-red-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-8 sm:mt-12 p-4 sm:p-6 bg-orange-100 rounded-xl max-w-2xl mx-auto">
            <p className="text-base sm:text-lg md:text-xl font-bold text-orange-800">
              Conclusion : Vous ne perdez pas du temps.<br />
              <span className="text-lg sm:text-xl md:text-2xl">Vous perdez de l'argent.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION RECADRAGE
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div>
              <Badge className="mb-3 sm:mb-4 text-xs sm:text-sm" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Perspective</Badge>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Ce n'est pas une rénovation.<br />
                <span className="text-orange-600">C'est une décision financière.</span>
              </h2>
              
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-4 sm:mb-6">
                Une rénovation aujourd'hui vous permet :
              </p>

              <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {[
                  'de réduire vos impôts',
                  'd\'augmenter la valeur de votre bien',
                  'd\'améliorer votre confort'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 sm:gap-3">
                    <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--sage)] flex-shrink-0" />
                    <span className="text-sm sm:text-base md:text-lg">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-gray-100 p-6 rounded-xl">
                <p className="text-lg font-medium text-gray-800">
                  Attendre revient à renoncer à un levier fiscal concret.
                </p>
                <p className="text-lg mt-3 font-semibold" style={{ color: 'var(--sage-dark)' }}>
                  La question n'est pas "est-ce que je fais les travaux ?"<br />
                  Mais <span className="text-orange-600">"quand est-ce que je les fais ?"</span>
                </p>
              </div>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Cuisine moderne rénovée" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-xl shadow-lg max-w-xs">
                <AlertTriangle className="h-8 w-8 mb-2" />
                <p className="font-bold">Fenêtre fiscale</p>
                <p className="text-sm text-white/80">Profitez-en avant qu'il ne soit trop tard</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SIMULATEUR INTÉGRÉ
          ============================================ */}
      <section className="py-20 bg-gray-50" ref={simulatorRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge className="mb-4 bg-orange-100 text-orange-700">Simulation rapide</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Combien allez-vous perdre si vous attendez ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Faites une estimation rapide en moins de 30 secondes.
                Découvrez l'impact concret de la réforme sur votre projet.
              </p>

              {/* Compte à rebours */}
              <FiscalCountdown />
            </div>

            <FiscalSimulator onCtaClick={scrollToForm} />
          </div>
        </div>
      </section>

      {/* ============================================
          COMPARAISON VISUELLE
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Faire maintenant vs Attendre
            </h2>
            <p className="text-lg text-muted-foreground">Même projet. Deux réalités.</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl">
              {/* Maintenant */}
              <div className="bg-green-500 text-white p-8">
                <h3 className="text-2xl font-bold mb-6">Aujourd'hui</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <Check className="h-6 w-6" />
                    <span>Déduction fiscale</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-6 w-6" />
                    <span>Coût optimisé</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-6 w-6" />
                    <span>Gain immédiat</span>
                  </li>
                </ul>
              </div>

              {/* Après réforme */}
              <div className="bg-red-500 text-white p-8">
                <h3 className="text-2xl font-bold mb-6">Après réforme</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <X className="h-6 w-6" />
                    <span>Aucune déduction</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <X className="h-6 w-6" />
                    <span>Coût total</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <X className="h-6 w-6" />
                    <span>Perte directe</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SERVICES AVEC IMPACT FISCAL
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Nos services</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Une rénovation optimisée pendant la fenêtre fiscale actuelle
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Nous réalisons vos travaux en tenant compte du contexte fiscal actuel.
              Objectif : maximiser votre avantage aujourd'hui, éviter une perte demain.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {fiscalExamples.map((example, idx) => {
              const economie = Math.round(example.budget * (example.taux / 100));
              const coutReel = example.budget - economie;
              
              return (
                <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-4">{example.service}</h3>
                    
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Budget travaux</span>
                        <span className="font-semibold">{example.budget.toLocaleString('fr-CH')} CHF</span>
                      </div>
                      
                      <div className="border-t pt-3">
                        <p className="text-sm font-medium text-green-700 mb-2">Aujourd'hui :</p>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Économie fiscale</span>
                          <span className="font-semibold text-green-600">-{economie.toLocaleString('fr-CH')} CHF</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Coût réel</span>
                          <span className="font-bold text-green-600">{coutReel.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                      
                      <div className="border-t pt-3">
                        <p className="text-sm font-medium text-red-700 mb-2">Après réforme :</p>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Coût réel</span>
                          <span className="font-bold text-red-600">{example.budget.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-orange-100 rounded-lg text-center">
                      <span className="text-orange-800 font-bold">
                        Différence : +{economie.toLocaleString('fr-CH')} CHF
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================
          LIENS VERS SOUS-PAGES SPÉCIFIQUES
          ============================================ */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Découvrez nos pages dédiées par type de rénovation
            </h2>
            <p className="text-muted-foreground">
              Informations détaillées et exemples concrets pour chaque projet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/renovation/cuisine" className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border hover:border-orange-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center">
                  <ChefHat className="h-7 w-7 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg group-hover:text-orange-600 transition-colors">Cuisine</h3>
                  <p className="text-sm text-muted-foreground">Économie jusqu'à 15'000 CHF</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Cuisine IKEA, sur mesure ou haut de gamme - optimisez fiscalement votre projet.
              </p>
              <span className="text-orange-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                En savoir plus <ArrowRight className="h-4 w-4" />
              </span>
            </Link>

            <Link to="/renovation/salle-de-bain" className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border hover:border-orange-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Bath className="h-7 w-7 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg group-hover:text-orange-600 transition-colors">Salle de bain</h3>
                  <p className="text-sm text-muted-foreground">Économie jusqu'à 11'000 CHF</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Douche italienne, double vasque, baignoire - transformez votre salle de bain.
              </p>
              <span className="text-orange-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                En savoir plus <ArrowRight className="h-4 w-4" />
              </span>
            </Link>

            <Link to="/renovation/complete" className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border hover:border-orange-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
                  <Home className="h-7 w-7 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg group-hover:text-orange-600 transition-colors">Rénovation complète</h3>
                  <p className="text-sm text-muted-foreground">Économie jusqu'à 45'000 CHF</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Appartement ou maison - projet clé en main avec coordination totale.
              </p>
              <span className="text-orange-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                En savoir plus <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================
          RÉALISATIONS
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-700">Réalisations</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Projets réalisés pendant la période de déduction fiscale
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Ces projets ont été réalisés dans un contexte fiscal avantageux.
              Aujourd'hui encore, il est possible d'optimiser ce type de rénovation.
              <strong className="block mt-2 text-orange-600">Demain, ce ne sera plus le cas.</strong>
            </p>
          </div>

          {/* Témoignages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                name: 'Philippe & Sandra K.', 
                city: 'Lausanne', 
                type: 'Rénovation cuisine',
                economie: '8\'750 CHF',
                text: 'Grâce à l\'optimisation fiscale, notre cuisine nous a coûté 26\'250 CHF au lieu de 35\'000 CHF. Merci Homely !'
              },
              { 
                name: 'Martine L.', 
                city: 'Nyon', 
                type: 'Salle de bain',
                economie: '6\'250 CHF',
                text: 'On hésitait à attendre. Heureusement qu\'on a fait les travaux maintenant, on a économisé plus de 6\'000 CHF d\'impôts.'
              },
              { 
                name: 'Famille Müller', 
                city: 'Genève', 
                type: 'Rénovation complète',
                economie: '20\'000 CHF',
                text: 'Rénovation complète de notre appartement. L\'économie fiscale a financé toute la cuisine équipée !'
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{review.name}</p>
                      <p className="text-xs text-muted-foreground">{review.type} • {review.city}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t">
                  <p className="text-sm text-green-600 font-semibold">
                    Économie réalisée : {review.economie}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          CTA FINAL - URGENCE
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} ref={formRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-orange-500">Estimation gratuite</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Chaque mois d'attente réduit votre avantage fiscal.
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Faites estimer votre projet gratuitement et découvrez combien vous pouvez 
                encore optimiser aujourd'hui.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Estimation de l\'économie fiscale',
                  'Devis détaillé sous 48h',
                  'Accompagnement optimisation fiscale',
                  'Sans engagement'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>

              {/* Compte à rebours */}
              <div className="mb-8">
                <FiscalCountdown />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800 font-medium">
                  Je veux optimiser mes travaux avant la réforme
                </p>
              </div>
              <ServiceLeadForm service="renover" pole="renovation" questions={questions} />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          FOOTER SPÉCIAL RÉNOVATION
          ============================================ */}
      <section className="py-6 bg-gray-100 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-center text-muted-foreground">
            Les règles fiscales évoluent suite à la réforme votée en Suisse en 2025.
            Les déductions actuelles restent applicables pendant la phase transitoire, 
            avec une entrée en vigueur estimée à partir de 2028.
            Les estimations présentées sont indicatives et doivent être validées au cas par cas.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};
